import React, { useState, useMemo, useCallback, useEffect } from "react";
import axios from "axios";
import Voltage from "./Voltage";
import PowerFactor from "./PowerFactor";
import Power from "./Power";
import Current from "./Current";
import Energy from "./Energy";
import ThdVoltage from "./ThdVoltage";
import ThdCurrent from "./ThdCurrent";
import "../../pages/InsightsTab.css";

function fmtPkr(val) {
  return Number(val || 0).toLocaleString("en-US");
}

function getUsername() {
  let user = null;
  try {
    const stored = localStorage.getItem("user");
    if (stored) user = JSON.parse(stored);
  } catch (e) {
    user = null;
  }
  return (
    localStorage.getItem("username") ||
    user?.username ||
    user?.userName ||
    user?.email ||
    ""
  );
}

function toIsoDate(date) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function defaultRange(days = 3) {
  const to = new Date();
  const from = new Date(to);
  from.setDate(from.getDate() - days + 1);
  return { from: toIsoDate(from), to: toIsoDate(to) };
}

const CATEGORIES = [
  "Voltage",
  "Current",
  "Power",
  "Power Factor",
  "Energy",
  "Frequency",
  "THD Voltage",
  "THD Current",
];

const Icon = ({ name, className, size = 24, color = "currentColor" }) => {
  const common = {
    width: size,
    height: size,
    fill: "none",
    viewBox: "0 0 24 24",
    stroke: color,
    strokeWidth: 1.8,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    className,
  };
  switch (name) {
    case "chart":
      return (<svg {...common}><path d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>);
    case "coin":
      return (<svg {...common}><path d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>);
    case "check":
      return (<svg {...common}><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>);
    case "info":
    default:
      return (<svg {...common}><path d="M12 16v-4m0-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>);
  }
};

export default function EnSightTab() {
  const initialRange = useMemo(() => defaultRange(3), []);

  const [fromDate, setFromDate] = useState(initialRange.from);
  const [toDate, setToDate] = useState(initialRange.to);
  const [machine, setMachine] = useState("All");
  const [severity, setSeverity] = useState("All");
  const [category, setCategory] = useState("Voltage");

  const [appliedFilters, setAppliedFilters] = useState({
    fromDate: initialRange.from,
    toDate: initialRange.to,
    machine: "All",
    severity: "All",
    category: "Voltage",
  });

  const [summary, setSummary] = useState({
    totalLoss: 0,
    totalCost: 0,
    totalSavings: 0,
    criticalCount: 0,
    warningCount: 0,
  });

  const [availableMachines, setAvailableMachines] = useState([]);

  // Fetch full list of client machines on mount
  useEffect(() => {
    const username = getUsername();
    if (!username) return;

    axios
      .get("/api/client-machines", { params: { username } })
      .then((res) => {
        if (res.data && Array.isArray(res.data.machines)) {
          const names = Array.from(
            new Set(
              res.data.machines
                .map((m) => m.machine_name || m.name)
                .filter(Boolean)
            )
          ).sort();
          setAvailableMachines(names);
        }
      })
      .catch((err) => {
        console.warn("Failed to fetch client machines list:", err);
      });
  }, []);

  const handleApply = () => {
    setAppliedFilters({
      fromDate,
      toDate,
      machine,
      severity,
      category,
    });
  };

  const applyQuickRange = (days) => {
    const { from: fromIso, to: toIso } = defaultRange(days);
    setFromDate(fromIso);
    setToDate(toIso);
    setAppliedFilters((prev) => ({
      ...prev,
      fromDate: fromIso,
      toDate: toIso,
    }));
  };

  const clearFilters = () => {
    const defaultR = defaultRange(3);
    setFromDate(defaultR.from);
    setToDate(defaultR.to);
    setMachine("All");
    setSeverity("All");
    setCategory("Voltage");
    setAppliedFilters({
      fromDate: defaultR.from,
      toDate: defaultR.to,
      machine: "All",
      severity: "All",
      category: "Voltage",
    });
  };

  const activeFilter = machine !== "All" || severity !== "All" || category !== "Voltage";

  const handleDataLoaded = useCallback((res) => {
    if (res && res.summary) {
      setSummary(res.summary);
    }
  }, []);

  return (
    <div className="insights-page">
      <div className="page">
        <div className="page-head">
          <div>
            <h1>Insights</h1>
            <div className="subtle">Power quality &amp; energy anomalies</div>
          </div>
          <div className="status-row">
            <span className="status-pill" style={{ color: "#dc2626" }}>
              <span className="dot" />{summary.criticalCount} Critical
            </span>
            <span className="status-pill" style={{ color: "#d97706" }}>
              <span className="dot" />{summary.warningCount} Warning
            </span>
          </div>
        </div>

        {/* Filter Controls Bar */}
        <section className="panel filters">
          <div className="filter-grid">
            <div className="field">
              <label>From</label>
              <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
            </div>
            <div className="field">
              <label>To</label>
              <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
            </div>
            <div className="quick-ranges">
              <button type="button" className="quick-btn" onClick={() => applyQuickRange(3)}>3D</button>
              <button type="button" className="quick-btn" onClick={() => applyQuickRange(7)}>7D</button>
              <button type="button" className="quick-btn" onClick={() => applyQuickRange(30)}>30D</button>
            </div>
            <div className="field">
              <label>Machine</label>
              <select value={machine} onChange={(e) => setMachine(e.target.value)}>
                <option value="All">All Machines</option>
                {availableMachines.map((name) => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
            </div>
            <div className="field">
              <label>Severity</label>
              <select value={severity} onChange={(e) => setSeverity(e.target.value)}>
                <option value="All">All Severities</option>
                <option value="Critical">Critical</option>
                <option value="Warning">Warning</option>
              </select>
            </div>
            <div className="field">
              <label>Category</label>
              <select value={category} onChange={(e) => setCategory(e.target.value)}>
                <option value="All">All Categories</option>
                {CATEGORIES.map((name) => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
            </div>
            <button type="button" className="primary-btn" onClick={handleApply}>Apply</button>
            {activeFilter && (
              <button type="button" className="ghost-btn" onClick={clearFilters}>Clear</button>
            )}
          </div>
        </section>

        {/* Summary Metric Cards */}
        <section className="summary-grid">
          <div className="stat-card" style={{ background: "#002f46" }}>
            <Icon name="chart" className="stat-icon" size={36} />
            <div>
              <p className="stat-label">Total Energy Loss</p>
              <div className="stat-value">PKR {fmtPkr(summary.totalLoss)}</div>
              <div className="stat-sub">In selected period</div>
            </div>
          </div>
          <div className="stat-card" style={{ background: "#ff9c07" }}>
            <Icon name="coin" className="stat-icon" size={36} />
            <div>
              <p className="stat-label">Total Cost Impact</p>
              <div className="stat-value">PKR {fmtPkr(summary.totalCost)}</div>
              <div className="stat-sub">Losses + penalties incurred</div>
            </div>
          </div>
          <div className="stat-card" style={{ background: "#009f8d" }}>
            <Icon name="check" className="stat-icon" size={36} />
            <div>
              <p className="stat-label">Total Savings Potential</p>
              <div className="stat-value">PKR {fmtPkr(summary.totalSavings)}</div>
              <div className="stat-sub">If all fixes are implemented</div>
            </div>
          </div>
        </section>

        {/* Dynamic Content: each category runs its own endpoint when selected */}
        {appliedFilters.category === "Voltage" ? (
          <Voltage
            fromDate={appliedFilters.fromDate}
            toDate={appliedFilters.toDate}
            machine={appliedFilters.machine}
            severity={appliedFilters.severity}
            category={appliedFilters.category}
            onDataLoaded={handleDataLoaded}
          />
        ) : appliedFilters.category === "Power Factor" ? (
          <PowerFactor
            fromDate={appliedFilters.fromDate}
            toDate={appliedFilters.toDate}
            machine={appliedFilters.machine}
            severity={appliedFilters.severity}
            category={appliedFilters.category}
            onDataLoaded={handleDataLoaded}
          />
        ) : appliedFilters.category === "Power" ? (
          <Power
            fromDate={appliedFilters.fromDate}
            toDate={appliedFilters.toDate}
            machine={appliedFilters.machine}
            severity={appliedFilters.severity}
            category={appliedFilters.category}
            onDataLoaded={handleDataLoaded}
          />
        ) : appliedFilters.category === "Current" ? (
          <Current
            fromDate={appliedFilters.fromDate}
            toDate={appliedFilters.toDate}
            machine={appliedFilters.machine}
            severity={appliedFilters.severity}
            category={appliedFilters.category}
            onDataLoaded={handleDataLoaded}
          />
        ) : appliedFilters.category === "Energy" ? (
          <Energy
            fromDate={appliedFilters.fromDate}
            toDate={appliedFilters.toDate}
            machine={appliedFilters.machine}
            severity={appliedFilters.severity}
            category={appliedFilters.category}
            onDataLoaded={handleDataLoaded}
          />
        ) : appliedFilters.category === "THD Voltage" ? (
          <ThdVoltage
            fromDate={appliedFilters.fromDate}
            toDate={appliedFilters.toDate}
            machine={appliedFilters.machine}
            severity={appliedFilters.severity}
            category={appliedFilters.category}
            onDataLoaded={handleDataLoaded}
          />
        ) : appliedFilters.category === "THD Current" ? (
          <ThdCurrent
            fromDate={appliedFilters.fromDate}
            toDate={appliedFilters.toDate}
            machine={appliedFilters.machine}
            severity={appliedFilters.severity}
            category={appliedFilters.category}
            onDataLoaded={handleDataLoaded}
          />
        ) : (
          <div className="panel empty-state">
            <div>
              <div className="empty-icon"><Icon name="info" size={28} /></div>
              <p className="empty-title">No insights detected in this period</p>
              {/* Was two contradictory sentences stacked on top of each other after a
                  merge; one list, kept in step with the branches above. */}
              <p className="empty-desc">
                Select Voltage, Current, Power, Power Factor, Energy, THD Voltage or THD Current to view insights
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
